import { api } from './api.ts'
import { askNotificationPermission, needsHomeScreenForNotifications } from './platform.ts'

/**
 * Web Push for the daily reminder (docs/PRD.md US-8.1). The server sends at the time set in Pengaturan; the service
 * worker shows it (public/push-sw.js). The VAPID public key comes from the API at runtime (no build variable).
 */

export type PushInfo = { ok: true; enabled: boolean; publicKey: string | null; devices: number }
export const PUSH_KEY = ['push'] as const
export const getPushInfo = () => api<PushInfo>('GET', '/api/push')
export const sendTestPush = () => api<{ ok: true; sent: number }>('POST', '/api/push/test')

export const pushSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && typeof Notification !== 'undefined'

/** The active service worker, or null when there is none (dev server, first load still installing past the wait). */
const activeRegistration = (waitMs = 8000) =>
  pushSupported()
    ? Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), waitMs))]).catch(() => null)
    : Promise.resolve(null)

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null
  const reg = await navigator.serviceWorker.getRegistration().catch(() => undefined)
  return reg ? reg.pushManager.getSubscription().catch(() => null) : null
}

const keyBytes = (b64url: string) => {
  const s = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64url.length + 3) % 4))
  return Uint8Array.from(s, (c) => c.charCodeAt(0))
}
const sameKey = (sub: PushSubscription, key: Uint8Array) => {
  const k = sub.options.applicationServerKey
  if (!k) return false
  const a = new Uint8Array(k)
  return a.length === key.length && a.every((v, i) => v === key[i])
}

export type PushOutcome = 'on' | 'denied' | 'unsupported' | 'unavailable' | 'error'

async function registerThisBrowser(): Promise<PushOutcome> {
  try {
    const reg = await activeRegistration()
    if (!reg) return 'unsupported'
    const info = await getPushInfo()
    if (!info.enabled || !info.publicKey) return 'unavailable'
    const key = keyBytes(info.publicKey)
    let sub = await reg.pushManager.getSubscription()
    // made with another server key (keys were replaced): it can never receive our pushes
    if (sub && !sameKey(sub, key)) {
      await sub.unsubscribe()
      sub = null
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
    await api('POST', '/api/push/subscribe', sub.toJSON())
    return 'on'
  } catch {
    return 'error'
  }
}

/** From a tap: the permission prompt comes first (while the tap still counts), then this browser is registered. */
export async function enablePush(): Promise<PushOutcome> {
  if (needsHomeScreenForNotifications() || !pushSupported()) return 'unsupported'
  const perm = await askNotificationPermission()
  if (perm === 'denied') return 'denied'
  if (perm !== 'granted') return perm === 'unsupported' ? 'unsupported' : 'error'
  return registerThisBrowser()
}

let synced = false
/**
 * Once per page load while signed in with the reminder on: permission given earlier (onboarding, another visit) →
 * keep this browser registered. Silent, never prompts.
 */
export function syncPushOnce() {
  if (synced || !pushSupported() || Notification.permission !== 'granted') return
  synced = true
  void registerThisBrowser()
}

/** Logout / account deleted: this browser stops getting this account's reminders. Never throws. */
export async function disablePush(opts: { server?: boolean } = {}) {
  const sub = await currentSubscription()
  if (!sub) return
  if (opts.server !== false) await api('POST', '/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {})
  await sub.unsubscribe().catch(() => {})
}

export const PUSH_OUTCOME_MSG: Record<PushOutcome, string> = {
  on: 'Notifikasi aktif di perangkat ini.',
  denied: 'Izin notifikasi ditolak. Izinkan lewat pengaturan situs di browser.',
  unsupported: 'Browser ini belum mendukung notifikasi.',
  unavailable: 'Notifikasi belum tersedia untuk sementara.',
  error: 'Notifikasi belum bisa diaktifkan. Coba lagi sebentar lagi.',
}
